import {
  AfterContentInit,
  Component,
  ContentChild,
  ContentChildren,
  EventEmitter,
  inject,
  Inject,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  DOCUMENT,
} from '@angular/core';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { getKeyCode, KEY_CODES } from '@backbase/ui-ang/util';
import { DomAttributesService } from '@backbase/ui-ang/services';
import { ModalHeaderComponent } from './modal-templates/modal-header.component';

import { ModalBodyComponent } from './modal-templates/modal-body.component';
import { BehaviorSubject, Subject, debounceTime, filter, merge, takeUntil, tap } from 'rxjs';
import { BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN } from '@backbase/ui-ang/money-protection';

/**
 * Component that displays a modal window with comprehensive features and accessibility support.
 *
 * @remarks
 * Provides modal functionality with customizable options, money protection banner support,
 * and automatic accessibility features. Handles modal lifecycle, keyboard interactions,
 * and integrates with ng-bootstrap modal service for robust modal management.
 *
 * ### Controlling components with overflow/z-index
 * If you have nested modals, dropdowns or select components,
 * add (or remove) the property `container="body"` on your topmost component.
 * You may have to toggle around depending on your setup or if your modal/dropdown/select has overflow.
 *
 * Alternatively, you can also add the `.modal-overflow` class by doing:
 * `[modalOptions]="{ modalDialogClass: 'modal-overflow' }"`.
 *
 * @example
 *
 * ```html
 * <bb-modal-ui [isOpen]="showModal" (cancel)="closeModal()" (confirm)="onConfirm()">
 *   <bb-modal-header-ui title="Cookies on Backbase"></bb-modal-header-ui>
 *   <bb-modal-body-ui>
 *     This website uses cookies to enhance your browsing experience.
 *   </bb-modal-body-ui>
 *   <bb-modal-footer-ui confirmText="Accept" (confirm)="saveAccept()">
 *   </bb-modal-footer-ui>
 * </bb-modal-ui>
 * ```
 */
@Component({
  selector: 'bb-modal-ui',
  templateUrl: './modal.component.html',
  standalone: false,
})
export class ModalComponent implements OnDestroy, AfterContentInit {
  constructor(
    private readonly modalService: NgbModal,
    private readonly renderer: Renderer2,
    private readonly domAttrService: DomAttributesService,
    // cannot use type `Document` due to compilation issues caused by `strictMetadataEmit`
    @Inject(DOCUMENT) private readonly document: any,
  ) {}

  /**
   * Reference to the opened modal instance for lifecycle management.
   */
  private modalRef: NgbModalRef | undefined;

  /**
   * Unique identifier for the modal component instance.
   */
  readonly id = this.domAttrService.generateId();

  /**
   * Internal BehaviorSubject to manage modal open state.
   */
  private readonly _isOpen = new BehaviorSubject(false);
  /**
   * The flag to show the dialog window. Defaults to 'false'.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal">
   * </bb-modal-ui>
   * ```
   */
  @Input()
  set isOpen(value: boolean) {
    this._isOpen.next(value);
  }

  /**
   * Gets the current open state of the modal.
   */
  get isOpen(): boolean {
    return this._isOpen.getValue();
  }
  /**
   * Set `aria-labelledby` with an element id that contains a brief label about the modal.
   * By default is takes the modal heading.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" aria-labelledby="modal-title-id">
   *   <bb-modal-header-ui id="modal-title-id" title="Confirmation"></bb-modal-header-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input('aria-labelledby')
  get ariaLabelledby(): string | undefined {
    return this._ariaLabelledby ?? this.modalOptions.ariaLabelledBy ?? this.modalHeader?.headerId;
  }

  /**
   * Sets the aria-labelledby value.
   */
  set ariaLabelledby(value: string | undefined) {
    this._ariaLabelledby = value;
  }

  private _ariaLabelledby: string | undefined;

  /**
   * Set `aria-describedby` with an element id that contains a detailed description about the modal.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" aria-describedby="modal-description">
   *   <bb-modal-body-ui id="modal-description">
   *     This modal contains important information.
   *   </bb-modal-body-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input('aria-describedby')
  get ariaDescribedby(): string | undefined {
    return this._ariaDescribedby ?? this.modalOptions.ariaDescribedBy ?? this.modalBody?.bodyId;
  }

  /**
   * Sets the aria-describedby value.
   */
  set ariaDescribedby(value: string | undefined) {
    this._ariaDescribedby = value;
  }

  private _ariaDescribedby: string | undefined;

  /**
   * Customize the ARIA role this component.
   *
   * This can be used to improve accessibility for components, for example by configuring `[role]="'alertdialog'"`
   * if it requires immediate attention of the user.
   *
   * Defaults to 'dialog'.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [role]="'alertdialog'">
   * </bb-modal-ui>
   * ```
   */
  @Input() role = 'dialog';
  /**
   * Dialog options inherited from https://ng-bootstrap.github.io/#/components/modal/api#NgbModalOptions. Defaults to an empty object.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [modalOptions]="{ backdrop: 'static' }">
   * </bb-modal-ui>
   * ```
   */
  @Input() modalOptions: NgbModalOptions = {};

  /**
   * The flag to show money protection insurance banner on top of the modal.
   * In order to see banner you will need to configure `BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN` to provide
   * component to render in banner content outlet.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" [showMoneyProtectionBanner]="true">
   *   <bb-modal-header-ui title="Payment"></bb-modal-header-ui>
   *   <bb-modal-body-ui>Payment details</bb-modal-body-ui>
   * </bb-modal-ui>
   * ```
   */
  @Input() showMoneyProtectionBanner = false;
  /**
   * Custom class to be assigned for money protection banner inside modal
   * @type {string}
   *
   * @example
   *
   * ```html
   * <bb-modal-ui
   *   [showMoneyProtectionBanner]="true"
   *   moneyProtectionBannerClasses="custom-banner-style">
   * </bb-modal-ui>
   * ```
   */
  @Input() moneyProtectionBannerClasses = '';
  /**
   * The event that's fired after confirm button is pressed. Can be used with
   * (click)="dialogRef.onConfirm()" where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" (confirm)="onConfirm()">
   *   <bb-modal-footer-ui confirmText="Save" (confirm)="onConfirm()">
   *   </bb-modal-footer-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() confirm = new EventEmitter();
  /**
   * The event that's fired after cancel button is pressed. Can be used with
   * (click)="dialogRef.onCancel()" where dialogRef is a templateRef on <bb-dialog-ui #dialogRef />
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [isOpen]="showModal" (cancel)="closeModal()">
   *   <bb-modal-footer-ui cancelText="Cancel" (cancel)="closeModal()">
   *   </bb-modal-footer-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() cancel = new EventEmitter();
  /**
   * Used for two way binding with the isOpen Input. If used, backdrop
   * and esc dismiss cases will be handled automatically.
   *
   * @example
   *
   * ```html
   * <bb-modal-ui [(isOpen)]="showModal">
   *   <bb-modal-header-ui title="Two-way Binding"></bb-modal-header-ui>
   * </bb-modal-ui>
   * ```
   */
  @Output() isOpenChange = new EventEmitter();

  @ViewChild('modalContent', { static: true }) modalContent: TemplateRef<any> | undefined;

  @ContentChild(ModalHeaderComponent) modalHeader: ModalHeaderComponent | undefined;
  @ContentChildren(ModalHeaderComponent) private modalHeaders: QueryList<ModalHeaderComponent> | undefined;
  @ContentChild(ModalBodyComponent) modalBody: ModalBodyComponent | undefined;

  /**
   * @internal
   */
  protected readonly moneyProtectionBanner? = inject(BB_MONEY_PROTECTION_BANNER_CONFIG_TOKEN, { optional: true });

  private emitConfirm = true;
  private headerChange$ = new Subject<void>();
  private destroy$ = new Subject<void>();

  ngAfterContentInit(): void {
    this.subscribeIsOpen();
    this.subscribeHeaderClose();
    this.modalHeaders?.changes
      .pipe(
        tap(() => {
          this.headerChange$.next();
        }),
        debounceTime(0),
        filter(Boolean),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.subscribeHeaderClose();
      });
  }

  /**
   * Handles modal dismiss reasons and emits isOpenChange event.
   *
   * @remarks
   * Used internally to manage modal dismiss behavior and notify parent components
   * of state changes. Automatically emits false to trigger two-way binding updates.
   *
   * @param reason - The reason for dismissing the modal
   * @returns The same dismiss reason passed in
   */
  getDismissReason(reason: ModalDismissReasons | string): ModalDismissReasons | string {
    this.isOpenChange.emit(false);

    return reason;
  }

  /**
   * Dismisses the modal with an optional reason.
   *
   * @remarks
   * Programmatically dismisses the modal, typically used for cancel operations
   * or when the modal should be closed without confirming any actions.
   *
   * @param reason - Optional reason for dismissing the modal
   */
  dismissModal(reason?: string): void {
    if (this.modalRef) this.modalRef.dismiss(reason);
  }

  /**
   * Closes the modal with an optional result.
   *
   * @remarks
   * Programmatically closes the modal, typically used for confirm operations
   * or when the modal should be closed with a successful result.
   *
   * @param reason - Optional result or reason for closing the modal
   */
  closeModal(reason?: string): void {
    if (this.modalRef) this.modalRef.close(reason);
  }

  /**
   * Sets whether the confirm event should be emitted when modal closes.
   *
   * @remarks
   * Used internally to control whether closing the modal should trigger
   * the confirm event. Helps distinguish between confirm and cancel actions.
   *
   * @param flag - Whether to emit confirm event on modal close
   */
  setConfirm(flag: boolean): void {
    this.emitConfirm = flag;
  }

  /**
   * Cleans up resources when component is destroyed.
   *
   * @remarks
   * Ensures proper cleanup of observables and closes any open modal instances
   * to prevent memory leaks and unexpected behavior.
   */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeModal();
  }

  /**
   * Handles keyboard events to manage modal focus trapping.
   *
   * @remarks
   * Prevents event propagation for all keys except Escape to maintain
   * proper focus management within the modal dialog.
   *
   * @param event - The keyboard event to handle
   */
  trapKeyEvent(event: KeyboardEvent) {
    if (getKeyCode(event) !== KEY_CODES.ESCAPE) {
      event.stopPropagation();
    }
  }

  private subscribeHeaderClose() {
    this.modalHeader?.close.pipe(takeUntil(merge(this.destroy$, this.headerChange$))).subscribe(() => {
      this.dismissModal();
    });
  }

  private subscribeIsOpen() {
    this._isOpen.pipe(filter(Boolean), takeUntil(this.destroy$)).subscribe(() => {
      this.setConfirm(true);

      this.modalBody?.setScrollable(this.modalOptions.scrollable);
      this.modalRef = this.modalService.open(this.modalContent, this.modalOptions);
      this.modalRef.result.then(
        (result) => {
          if (this.emitConfirm) {
            this.confirm.emit(result);
          }
        },
        (reason) => this.cancel.emit(this.getDismissReason(reason)),
      );

      const modalContent = (this.document as Document).getElementsByClassName('modal-dialog');
      const modal = (this.document as Document).getElementsByClassName('modal');
      modalContent?.[0]?.removeAttribute('role');
      modal?.[0]?.scrollTo?.(0, 0);

      // remove some a11y attributes for ngb-modal since we  already set it on the HTML
      // to allow better a11y support
      const ngbModalWindow = (this.document as Document).querySelector('ngb-modal-window');
      this.renderer.removeAttribute(ngbModalWindow, 'role');
      this.renderer.removeAttribute(ngbModalWindow, 'aria-modal');
    });

    this._isOpen
      .pipe(
        filter((value) => !value && !!this.modalRef),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.setConfirm(false);
        this.modalRef!.close();
      });
  }
}
